import * as React from 'react';
import { classes, style } from 'typestyle';
import { TrackSchema } from '../../../types/schema';
import { percent, px } from 'csx';
import { mixinTextEllipsis } from '../../../theme/mixin';
import { UtmLink } from '../atoms/UtmLink';
import { colorDisabledImage, colorGunmetal, colorNeutralLight, rebrand } from '../../../theme/color';
import { zLabel } from '../../../theme/z';
import { SubscriptionLabel } from '../atoms/SubscriptionLabel';
import { AnalyticsContextProvider } from '../utilities/AnalyticsContext';
import { CarouselImage } from '../atoms/CarouselImage';
import { Tooltip } from '../atoms/Tooltip';
import { InfoOutline } from '../atoms/icons/InfoOutline';
import { borderRadius } from '../../../theme/border';
import { NewLabel } from '../atoms/NewLabel';
import { ClockUnion } from '../atoms/icons/ClockUnion';
import { spacing } from '../../../theme/spacing';

const styles = {
  wrapper: style({
    position: 'relative',
    $nest: {
      '& .favourites-button': {
        position: 'absolute',
        top: px(8),
        right: px(8),
        zIndex: zLabel,
      },
      '& .play-button': {
        position: 'absolute',
        bottom: px(8),
        right: px(8),
        zIndex: zLabel,
      },
      '& .basket': {
        position: 'absolute',
        top: 'calc(50%)',
        left: '50%',
        zIndex: zLabel,
        display: 'none',
        transform: ' translate(-50%, -50%)',
      },
      '&:hover .basket': {
        display: 'flex',
      },
    },
  }),
  img: style({
    borderRadius: borderRadius.SMALL,
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  }),
  trackInfo: style({
    marginTop: spacing.XSMALL,
    width: percent(100),
  }),
  trackInfoLargeSpaced: style({
    marginTop: spacing.SMALL,
  }),
  unavailableText: style({
    color: rebrand.neutralOnLight[20].toHexString(),
    $nest: {
      '& *': {
        color: 'inherit',
      },
    },
  }),
  trackName: style(mixinTextEllipsis, {
    display: 'block',
    lineHeight: 'normal',
    color: colorGunmetal.toString(),
    fontWeight: 700,
    $nest: {
      '& a': {
        color: 'inherit',
        $nest: {
          '&:hover, &:focus': {
            textDecoration: 'underline',
          },
        },
      },
    },
  }),
  artistName: style(mixinTextEllipsis, {
    fontWeight: 400,
    marginTop: px(2),
    color: colorNeutralLight.toString(),
  }),
  trackLink: style({
    color: 'inherit',
  }),
  overlayContent: style({ $nest: { '& > *': { zIndex: zLabel } } }),
  imageSearchPage: style(),
  trackUnavailableimage: style({
    position: 'relative',
    $nest: {
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        background: colorDisabledImage.toString(),
      },
    },
  }),
  info: style({
    position: 'absolute',
    top: '8px',
    zIndex: zLabel,
    right: '5px',
  }),
  imageWrapper: style({
    position: 'relative',
    width: '100%',
    paddingBottom: '100%',
    borderRadius: borderRadius.MEDIUM,
    overflow: 'hidden',
  }),
};

export type TrackTileProps = {
  className?: string;
  track?: TrackSchema;
  isSearchPage?: boolean;
  fontSize?: 'normal' | 'large';
  onClick?: () => void;
};

export const TrackTile: React.FC<TrackTileProps> = ({
  children,
  className,
  track,
  isSearchPage = false,
  fontSize = 'normal',
  onClick,
}) => {
  const artists = track ? track.artists.map((artist) => artist.name).join(', ') : null;

  if (track) {
    const trackIsavailable = track.is_available_in_territory === null ? true : track.is_available_in_territory;
    const trackUrl = `/music/artists/${track.artists[0].slug}/track/${track.slug}`;

    return (
      <AnalyticsContextProvider
        context={{ trackIdentity: track.identity, trackTitle: track.title, trackArtist: artists }}
      >
        <div className={classes(styles.wrapper, className)} data-test-track-carousel={track.identity}>
          <UtmLink
            {...(isSearchPage ? { 'data-test-top-solution-text-link': 'track' } : {})}
            href={trackUrl}
            className={classes(styles.trackLink, !trackIsavailable && styles.trackUnavailableimage)}
            onClick={onClick}
            aria-label={`Link to ${track.title}`}
          >
            <div className={styles.imageWrapper}>
              <CarouselImage
                className={classes(styles.img, isSearchPage && styles.imageSearchPage)}
                alt={`Artwork for "${track.title}" by ${artists}`}
                identity={track.images.identity}
              />

              <span className={styles.overlayContent}>
                {trackIsavailable ? (
                  <>
                    <SubscriptionLabel included={track.is_stock_music} />
                    {track.is_newly_activated && <NewLabel icon={<ClockUnion />} text={'New on Lickd'} />}
                    {children}
                  </>
                ) : (
                  <div className={styles.info}>
                    <Tooltip positions={['top']} content={<span> Not available in your region</span>}>
                      <InfoOutline />
                    </Tooltip>
                  </div>
                )}
              </span>
            </div>
          </UtmLink>

          <div
            className={classes(
              styles.trackInfo,
              isSearchPage && styles.trackInfoLargeSpaced,
              !trackIsavailable && styles.unavailableText
            )}
          >
            <div className={classes(styles.trackName, 'track-name')}>
              <UtmLink href={trackUrl}>
                <Tooltip positions={['top']} align={'start'} content={<span>{track.title}</span>}>
                  {track.title}
                </Tooltip>
              </UtmLink>
            </div>
            <div className={styles.artistName}>{artists}</div>
          </div>
        </div>
      </AnalyticsContextProvider>
    );
  } else {
    return (
      <div className={styles.imageWrapper}>
        <CarouselImage className={classes(styles.img, isSearchPage && styles.imageSearchPage)} alt={' '} />
      </div>
    );
  }
};
